import { useEffect, useMemo, useState } from "react";
import { getStoreLogo, getStoreName } from "../../utils/helpers/storeSetting";
import { makeBarcodeSvg, makeQrMatrix, qrMatrixToSvg, svgToDataUrl } from "../../utils/helpers/memberCode";
import authorizedAxios from "../../helpers/authorizedAxios";

const escapeXml = (value = "") =>
  String(value || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

const sanitizeFileName = (value = "") =>
  String(value || "member-card")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");

const imageBlobToDataUrl = (blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });

const getStoreLogoPath = (setting = {}) => setting?.logo_path || setting?.logo || setting?.logo_url || "";

const MemberCardModal = ({ customer, storeSetting }) => {
  const storeName = getStoreName(storeSetting);
  const storeLogo = getStoreLogo(storeSetting);
  const storeLogoPath = getStoreLogoPath(storeSetting);
  const memberId = customer?.phone || "";
  const memberCode = String(memberId || "").trim();
  const barcodeValue = memberCode.replace(/\D/g, "") || memberCode;
  const [logoDataUrl, setLogoDataUrl] = useState("");
  const [isPreparingLogo, setIsPreparingLogo] = useState(false);

  const qrSvg = useMemo(() => {
    if (!memberCode) return "";
    return qrMatrixToSvg(makeQrMatrix(memberCode), { cell: 7, dark: "#064e3b" });
  }, [memberCode]);

  const barcodeSvg = useMemo(() => {
    if (!barcodeValue) return "";
    return makeBarcodeSvg(barcodeValue, { height: 58, color: "#064e3b" });
  }, [barcodeValue]);

  const qrDataUrl = qrSvg ? svgToDataUrl(qrSvg) : "";
  const barcodeDataUrl = barcodeSvg ? svgToDataUrl(barcodeSvg) : "";

  useEffect(() => {
    let isMounted = true;

    const loadLogo = async () => {
      if (!storeLogo) {
        setLogoDataUrl("");
        return;
      }

      if (storeLogo.startsWith("data:")) {
        setLogoDataUrl(storeLogo);
        return;
      }

      setIsPreparingLogo(true);
      try {
        const response = await fetch(storeLogo, { mode: "cors", credentials: "omit" });
        if (!response.ok) throw new Error("Logo fetch failed");
        const dataUrl = await imageBlobToDataUrl(await response.blob());
        if (isMounted) setLogoDataUrl(dataUrl);
      } catch (_error) {
        try {
          const response = await authorizedAxios.get("/stores/asset-data", {
            params: { path: storeLogoPath },
          });
          if (isMounted) setLogoDataUrl(response?.data?.data?.data_url || "");
        } catch (_fallbackError) {
          if (isMounted) setLogoDataUrl("");
        }
      } finally {
        if (isMounted) setIsPreparingLogo(false);
      }
    };

    loadLogo();

    return () => {
      isMounted = false;
    };
  }, [storeLogo, storeLogoPath]);

  const buildMemberCardSvg = () => {
    const width = 920;
    const height = 560;
    const logoMarkup = logoDataUrl
      ? `<rect x="64" y="52" width="136" height="82" rx="18" fill="#ffffff"/><image href="${escapeXml(logoDataUrl)}" x="76" y="62" width="112" height="62" preserveAspectRatio="xMidYMid meet" />`
      : `<text x="64" y="96" fill="#ffffff" font-size="30" font-weight="800">${escapeXml(storeName)}</text>`;

    return `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}">
      <defs>
        <style>
          text { font-family: Poppins, Roboto, Arial, sans-serif; }
        </style>
        <linearGradient id="memberGradient" x1="0" x2="1" y1="0" y2="1">
          <stop offset="0%" stop-color="#059669"/>
          <stop offset="52%" stop-color="#047857"/>
          <stop offset="100%" stop-color="#0f766e"/>
        </linearGradient>
        <filter id="shadow" x="-10%" y="-10%" width="120%" height="120%">
          <feDropShadow dx="0" dy="22" stdDeviation="24" flood-color="#064e3b" flood-opacity="0.24"/>
        </filter>
      </defs>
      <rect width="920" height="560" rx="42" fill="#f8fafc"/>
      <rect x="32" y="32" width="856" height="496" rx="38" fill="url(#memberGradient)" filter="url(#shadow)"/>
      <circle cx="760" cy="80" r="220" fill="#ffffff" opacity="0.08"/>
      <circle cx="192" cy="512" r="260" fill="#ffffff" opacity="0.07"/>
      ${logoMarkup}
      <text x="64" y="180" fill="#d1fae5" font-size="22" font-weight="700" letter-spacing="2">MEMBER CARD</text>
      <text x="64" y="234" fill="#ffffff" font-size="42" font-weight="900">${escapeXml(customer?.name || "Customer")}</text>
      <text x="64" y="278" fill="#d1fae5" font-size="23" font-weight="600">${escapeXml(customer?.category_name || customer?.category || "Member")}</text>
      <text x="64" y="358" fill="#d1fae5" font-size="18" font-weight="700" letter-spacing="1.5">MEMBER ID</text>
      <text x="64" y="400" fill="#ffffff" font-size="30" font-weight="800" letter-spacing="2">${escapeXml(memberCode || "-")}</text>
      <text x="64" y="466" fill="#d1fae5" font-size="18" font-weight="700">CITY</text>
      <text x="128" y="466" fill="#ffffff" font-size="24" font-weight="900">${escapeXml(customer?.city_name || "-")}</text>
      <rect x="642" y="78" width="190" height="190" rx="26" fill="#ffffff"/>
      ${qrDataUrl ? `<image href="${escapeXml(qrDataUrl)}" x="666" y="102" width="142" height="142"/>` : ""}
      <rect x="560" y="338" width="284" height="102" rx="18" fill="#ffffff"/>
      ${barcodeDataUrl ? `<image href="${escapeXml(barcodeDataUrl)}" x="594" y="358" width="216" height="46" preserveAspectRatio="xMidYMid meet"/>` : ""}
      <text x="702" y="424" text-anchor="middle" fill="#064e3b" font-size="18" font-weight="800" letter-spacing="1.3">${escapeXml(barcodeValue || "-")}</text>
      <text x="702" y="482" text-anchor="middle" fill="#d1fae5" font-size="18" font-weight="700">${escapeXml(storeName)}</text>
    </svg>`;
  };

  const memberCardSvg = useMemo(
    () => buildMemberCardSvg(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [logoDataUrl, storeName, customer, memberCode, barcodeValue, qrDataUrl, barcodeDataUrl],
  );
  const memberCardPreviewUrl = useMemo(() => svgToDataUrl(memberCardSvg), [memberCardSvg]);

  const downloadBlob = (blob, extension = "svg") => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${sanitizeFileName(customer?.name || memberCode)}-member-card.${extension}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const downloadSvg = (svg) => {
    downloadBlob(new Blob([svg], { type: "image/svg+xml;charset=utf-8" }), "svg");
  };

  const handleDownload = () => {
    const svg = memberCardSvg;
    const image = new Image();
    const url = svgToDataUrl(svg);

    image.onload = () => {
      try {
        const canvas = document.createElement("canvas");
        canvas.width = 920;
        canvas.height = 560;
        const context = canvas.getContext("2d");
        context.drawImage(image, 0, 0);
        canvas.toBlob((blob) => {
          if (blob) {
            downloadBlob(blob, "png");
          } else {
            downloadSvg(svg);
          }
        }, "image/png");
      } catch (_error) {
        downloadSvg(svg);
      }
    };

    image.onerror = () => downloadSvg(svg);
    image.src = url;
  };

  return (
    <div
      className="modal modal-blur fade"
      id="modal-member-card"
      tabIndex="-1"
      role="dialog"
      aria-hidden="true"
      style={{
        backgroundColor: "rgba(0, 0, 0, 0.3)",
      }}>
      <div className="modal-dialog modal-dialog-centered modal-lg" role="document">
        <div className="modal-content">
          <div className="modal-header">
            <div>
              <h5 className="modal-title">Kartu Member</h5>
              <div className="text-secondary small">QR code dan barcode berisi nomor HP customer.</div>
            </div>
            <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div className="modal-body">
            <div className="mx-auto" style={{ maxWidth: 720 }}>
              {isPreparingLogo ? (
                <div className="alert alert-light border mb-3 py-2">Menyiapkan logo toko untuk kartu member...</div>
              ) : null}
              <div className="rounded-4 overflow-hidden" style={{ boxShadow: "0 2rem 4rem rgba(5, 150, 105, 0.18)" }}>
                <img src={memberCardPreviewUrl} alt="Kartu member" className="w-100 d-block" />
              </div>
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-link link-secondary" data-bs-dismiss="modal">
              Tutup
            </button>
            <button type="button" className="btn btn-primary ms-auto" onClick={handleDownload} disabled={!memberCode}>
              Download Gambar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MemberCardModal;
